import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../../config/config';

// Suggested NL questions the user can click to pre-fill
const getSuggestedQuestions = (repoName) => [
  `Which packages in ${repoName || 'this repo'} have critical or high severity CVEs?`,
  `Show all packages in ${repoName || 'this repo'} that have a fix available for their vulnerability`,
  `How many packages in ${repoName || 'this repo'} have no known license?`,
  `List all SBOM quality issues for ${repoName || 'this repo'} ordered by severity`,
  `Which packages in ${repoName || 'this repo'} have a CVSS score above 8.0?`,
];

function RiskBadge({ level }) {
  const colours = {
    CRITICAL: { bg: '#dc2626', text: '#fff' },
    HIGH:     { bg: '#ea580c', text: '#fff' },
    MEDIUM:   { bg: '#d97706', text: '#fff' },
    LOW:      { bg: '#16a34a', text: '#fff' },
  };
  const c = colours[level] || colours.LOW;
  return (
    <span style={{
      background: c.bg, color: c.text,
      borderRadius: 4, padding: '1px 6px', fontSize: '0.65rem', fontWeight: 600,
    }}>
      {level}
    </span>
  );
}

function SummaryView({ result }) {
  const riskColour = {
    CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#d97706', LOW: '#16a34a',
  }[result.riskLevel] || '#6b7280';

  const progressBg = riskColour;

  return (
    <div>
      {/* Headline */}
      <h6 className="fw-bold mb-2" style={{ lineHeight: 1.4 }}>{result.headline}</h6>

      {/* Risk score bar */}
      {result.riskScore !== undefined && (
        <div className="mb-3">
          <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem', marginBottom: 4 }}>
            <span className="text-secondary">Overall Risk Score</span>
            <span className="fw-semibold" style={{ color: riskColour }}>
              {result.riskScore}/100 &mdash; {result.riskLevel || 'UNKNOWN'}
            </span>
          </div>
          <div style={{ height: 6, background: 'var(--bs-border-color, #dee2e6)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(result.riskScore, 100)}%`, background: progressBg, borderRadius: 3, transition: 'width 0.6s ease' }} />
          </div>
        </div>
      )}

      {/* Summary paragraph */}
      <p className="small text-secondary mb-3" style={{ lineHeight: 1.55 }}>{result.summary}</p>

      {/* Critical findings */}
      {result.criticalFindings?.length > 0 && (
        <div className="mb-3">
          <div className="fw-semibold small mb-2">&#x1F6A8; Critical Findings ({result.criticalFindings.length})</div>
          {result.criticalFindings.map((f, i) => (
            <div key={i} className="mb-2 p-2 rounded" style={{ background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)' }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <RiskBadge level={f.severity} />
                <span className="small fw-semibold" style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{f.id}</span>
                {f.cvss > 0 && <span className="small text-secondary ms-auto" style={{ fontSize: '0.68rem' }}>CVSS {f.cvss.toFixed(1)}</span>}
              </div>
              <div className="small text-secondary" style={{ fontSize: '0.75rem' }}>&#x1F4E6; {f.package}</div>
              {f.summary && <div className="small mt-1" style={{ fontSize: '0.72rem', color: 'var(--bs-secondary-color)' }}>{f.summary}</div>}
              {f.fixedIn && <div className="small mt-1" style={{ color: '#16a34a', fontSize: '0.72rem' }}>&#x2705; Fix available: {f.fixedIn}</div>}
            </div>
          ))}
        </div>
      )}

      {/* License risks */}
      {result.licenseRisks?.length > 0 && (
        <div className="mb-3">
          <div className="fw-semibold small mb-2">&#x2696;&#xFE0F; License Risks</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {result.licenseRisks.map((l, i) => (
              <span key={i} title={`${l.risk} \u2014 ${l.package}`}
                style={{
                  fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4,
                  background: l.risk === 'STRONG_COPYLEFT' ? 'rgba(220,38,38,0.12)' : 'rgba(217,119,6,0.12)',
                  color: l.risk === 'STRONG_COPYLEFT' ? '#dc2626' : '#b45309',
                  border: `1px solid ${l.risk === 'STRONG_COPYLEFT' ? 'rgba(220,38,38,0.3)' : 'rgba(217,119,6,0.3)'}`,
                  cursor: 'default',
                }}>
                {l.license}
              </span>
            ))}
          </div>
          <div className="mt-1" style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>
            {result.licenseRisks.map(l => l.package).join(', ')}
          </div>
        </div>
      )}

      {/* Quality insights */}
      {result.qualityInsights?.length > 0 && (
        <div className="mb-3">
          <div className="fw-semibold small mb-2">&#x1F4CA; Quality Insights</div>
          <ul className="mb-0 ps-3" style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', lineHeight: 1.55 }}>
            {result.qualityInsights.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}

      {/* Top actions */}
      {result.topActions?.length > 0 && (
        <div className="mb-3">
          <div className="fw-semibold small mb-2">&#x2705; Priority Actions</div>
          <ol className="mb-0 ps-3" style={{ fontSize: '0.75rem', lineHeight: 1.65 }}>
            {result.topActions.map((a, i) => <li key={i}>{a}</li>)}
          </ol>
        </div>
      )}

      {/* EU CRA readiness */}
      {result.craReadiness && (
        <div className="p-2 rounded small" style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.2)', fontSize: '0.75rem', lineHeight: 1.5 }}>
          <span className="fw-semibold">&#x1F1EA;&#x1F1FA; EU CRA: </span>{result.craReadiness}
        </div>
      )}
    </div>
  );
}

function AiAssistantPanel({ repoName, sbomId, healthStats }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('summary');

  // Ask tab state
  const [question, setQuestion] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState('');

  // Summary tab state
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);
  const [summaryError, setSummaryError] = useState('');

  // Track which sbomId was last auto-triggered to avoid duplicate calls
  const lastAutoSbomRef = useRef(null);

  const triggerSummary = useCallback((repoN, sbomI) => {
    setSummaryResult(null);
    setSummaryError('');
    setSummaryLoading(true);
    axios.post(`${config.BASE_URL}/ai/summary`, {
      repoName: repoN || '',
      sbomId: sbomI || '',
    })
      .then(r => setSummaryResult(r.data))
      .catch(e => setSummaryError(e.response?.data?.error || e.message))
      .finally(() => setSummaryLoading(false));
  }, []);

  // Auto-generate summary when panel opens for a new SBOM
  useEffect(() => {
    if (!open || !sbomId) return;
    const key = `${repoName}__${sbomId}`;
    if (key === lastAutoSbomRef.current) return;
    lastAutoSbomRef.current = key;
    triggerSummary(repoName, sbomId);
  }, [open, sbomId, repoName, triggerSummary]);

  const handleAsk = () => {
    if (!question.trim()) return;
    setQueryLoading(true);
    setQueryResult(null);
    setQueryError('');
    axios.post(`${config.BASE_URL}/ai/query`, {
      question,
      repoName: repoName || '',
      sbomId: sbomId || '',
    })
      .then(r => setQueryResult(r.data))
      .catch(e => setQueryError(e.response?.data?.error || e.message))
      .finally(() => setQueryLoading(false));
  };

  const handleSummary = () => triggerSummary(repoName, sbomId);

  const suggestedQuestions = getSuggestedQuestions(repoName);

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 52,
          height: 52,
          borderRadius: '50%',
          fontSize: '1.5rem',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          background: '#0d6efd',
          color: '#fff',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="AI Assistant"
      >
        &#x1F916;
      </button>

      {/* Slide-in panel */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: open ? 0 : -440,
        width: 420,
        height: '100vh',
        background: 'var(--bs-body-bg, #fff)',
        borderLeft: '1px solid var(--bs-border-color, #dee2e6)',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
        zIndex: 1049,
        transition: 'right 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pointerEvents: open ? 'auto' : 'none',
        visibility: open ? 'visible' : 'hidden',
      }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-primary text-white">
          <div>
            <span className="fw-semibold">&#x1F916; {t('ai.assistantTitle', 'AI Assistant')}</span>
            {repoName && (
              <div style={{ fontSize: '0.7rem', opacity: 0.8, marginTop: 1 }}>
                {repoName}{sbomId ? ` \u00b7 ${sbomId.substring(0, 8)}` : ''}
              </div>
            )}
          </div>
          <button className="btn-close btn-close-white btn-sm" onClick={() => setOpen(false)} />
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs px-2 pt-2 border-bottom-0" style={{ flexShrink: 0 }}>
          {[
            { id: 'summary', label: `&#x1F4CA; ${t('ai.tabSummary', 'Summary')}` },
            { id: 'ask',     label: `&#x1F4AC; ${t('ai.tabAsk', 'Ask')}` },
          ].map(({ id, label }) => (
            <li key={id} className="nav-item">
              <button
                className={`nav-link py-1 px-3 ${tab === id ? 'active fw-semibold' : 'text-secondary'}`}
                onClick={() => setTab(id)}
                dangerouslySetInnerHTML={{ __html: label }}
              />
            </li>
          ))}
        </ul>

        {/* Tab content */}
        <div className="flex-grow-1 overflow-auto p-3">

          {/* Summary tab */}
          {tab === 'summary' && (
            <div>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <p className="text-secondary small mb-0">
                  AI-powered health summary for <strong>{repoName || 'this repository'}</strong>
                  {!sbomId && <span className="text-warning ms-1">(select an SBOM first)</span>}
                </p>
                <button
                  className="btn btn-outline-primary btn-sm"
                  style={{ fontSize: '0.72rem', padding: '2px 8px', flexShrink: 0 }}
                  onClick={handleSummary}
                  disabled={summaryLoading || !sbomId}
                  title="Re-generate summary"
                >
                  {summaryLoading ? <span className="spinner-border spinner-border-sm" /> : '\u21bb'}
                </button>
              </div>

              {summaryLoading && (
                <div className="text-center py-4 text-secondary">
                  <div className="spinner-border spinner-border-sm mb-2" />
                  <div className="small">Analysing {repoName}&hellip;</div>
                  <div style={{ fontSize: '0.7rem', marginTop: 4, opacity: 0.7 }}>
                    Fetching vulnerabilities, licenses &amp; quality&hellip;
                  </div>
                </div>
              )}

              {summaryError && (
                <div className="alert alert-danger py-2 small">{summaryError}</div>
              )}

              {summaryResult && !summaryLoading && <SummaryView result={summaryResult} />}

              {!summaryResult && !summaryLoading && !summaryError && sbomId && (
                <div className="text-center py-4 text-secondary small">
                  <div style={{ fontSize: '2rem' }}>&#x1F916;</div>
                  <div>Loading security summary&hellip;</div>
                </div>
              )}
            </div>
          )}

          {/* Ask tab */}
          {tab === 'ask' && (
            <div>
              <p className="text-secondary small mb-2">
                {t('ai.askHint', "Ask a natural-language question about this repository's dependencies.")}
              </p>

              {/* Suggested questions */}
              <div className="mb-3">
                <div className="small fw-semibold text-secondary mb-1" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suggestions</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setQuestion(q)}
                      style={{
                        textAlign: 'left', background: 'transparent',
                        border: '1px solid var(--bs-border-color, #dee2e6)',
                        borderRadius: 6, padding: '4px 10px', fontSize: '0.72rem',
                        cursor: 'pointer', color: 'var(--bs-body-color)',
                        lineHeight: 1.4,
                      }}
                      title="Click to use this question"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                className="form-control mb-2"
                rows={3}
                placeholder={t('ai.askPlaceholder', 'e.g. Which packages have critical CVEs?')}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleAsk(); }}
                style={{ fontSize: '0.85rem' }}
              />
              <button
                className="btn btn-primary btn-sm mb-3"
                onClick={handleAsk}
                disabled={queryLoading || !question.trim()}
              >
                {queryLoading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                {queryLoading ? t('ai.generating', 'Generating\u2026') : t('ai.submit', 'Ask')}
              </button>

              {queryError && <div className="alert alert-danger py-2 small">{queryError}</div>}

              {queryResult && (
                <div>
                  <p className="small mb-2" style={{ lineHeight: 1.55 }}>{queryResult.answer}</p>
                  {queryResult.sqlUsed && (
                    <details className="mb-2">
                      <summary className="small text-secondary" style={{ cursor: 'pointer' }}>
                        {t('ai.sqlUsed', 'SQL used')}
                      </summary>
                      <pre className="bg-dark text-light p-2 rounded small mt-1" style={{ fontSize: '0.65rem', overflowX: 'auto' }}>
                        {queryResult.sqlUsed}
                      </pre>
                    </details>
                  )}
                  {queryResult.data?.length > 0 && (
                    <div className="table-responsive" style={{ maxHeight: 280 }}>
                      <table className="table table-sm table-hover small mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            {Object.keys(queryResult.data[0]).map(k => (
                              <th key={k} style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {queryResult.data.map((row, i) => (
                            <tr key={i}>
                              {Object.values(row).map((v, j) => (
                                <td key={j} style={{ fontSize: '0.72rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {v == null ? '\u2014' : String(v)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AiAssistantPanel;
